import { Link } from '@tanstack/react-router'
import { AlertTriangle, ArrowRight, CalendarClock, Cpu, MonitorPlay, OctagonAlert, Siren } from 'lucide-react'
import { EmptyState } from '@/components/empty-state'
import { StatusDot } from '@/components/status-dot'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate, formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { CurrentlyPlaying, DashboardAlert, UpcomingCampaign } from '@/types/api'
import { playingSummary, versionShares } from '../lib/operations'

/** What online screens are showing right now (from live heartbeats). */
export function CurrentlyPlayingCard({ items, online }: { items: CurrentlyPlaying[] | undefined; online: number | undefined }) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle>Currently playing</CardTitle>
          <p className="text-[13px] text-muted-foreground">Live from your online screens</p>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/displays" search={{ connectivity: 'online' }}>
            All online <ArrowRight />
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="pb-3">
        {!items ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            compact
            icon={MonitorPlay}
            title={online ? 'No live playback reported yet' : 'No screens online'}
            description={online ? 'Players report what they show with each heartbeat.' : 'When players are online, what they show appears here.'}
          />
        ) : (
          <>
            <ul className="grid gap-2 sm:grid-cols-2">
              {items.map((p) => {
                const { title, detail } = playingSummary(p)
                return (
                  <li key={p.display.id}>
                    <Link
                      to="/displays/$displayId"
                      params={{ displayId: p.display.id }}
                      className="flex items-start gap-3 rounded-lg border px-3 py-2.5 outline-none hover:border-primary/30 hover:bg-muted/40 focus-visible:ring-[3px] focus-visible:ring-ring"
                    >
                      {p.mode === 'emergency' ? <Siren className="mt-0.5 size-4 shrink-0 text-danger" /> : <StatusDot status={p.mode === 'safe_mode' ? 'warning' : 'online'} className="mt-1.5" />}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] text-muted-foreground">{p.display.name}</span>
                        <span className="block truncate text-sm font-medium">{title}</span>
                        <span className="block truncate text-xs text-muted-foreground">{detail}</span>
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
            {online !== undefined && online > items.length && <p className="mt-2 text-xs text-muted-foreground">and {online - items.length} more online</p>}
          </>
        )}
      </CardContent>
    </Card>
  )
}

const SEVERITY_ICON = { info: AlertTriangle, warning: AlertTriangle, error: OctagonAlert, critical: Siren } as const

/** Latest unread warnings for the current user (hidden when there are none). */
export function AlertsCard({ alerts }: { alerts: DashboardAlert[] | undefined }) {
  if (!alerts || alerts.length === 0) return null
  return (
    <Card className="border-warning/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="size-4 text-warning" /> Alerts
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-3">
        <ul className="-mx-2">
          {alerts.map((a) => {
            const Icon = SEVERITY_ICON[a.severity]
            return (
              <li key={a.id} className="flex items-start gap-3 rounded-lg px-2 py-2">
                <Icon className={cn('mt-0.5 size-4 shrink-0', a.severity === 'warning' ? 'text-warning' : 'text-danger')} aria-label={a.severity} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{a.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {formatRelative(a.created_at)} · {a.body}
                  </span>
                </span>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}

export function UpcomingCampaignsCard({ campaigns }: { campaigns: UpcomingCampaign[] | undefined }) {
  if (!campaigns) return null
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Upcoming campaigns</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/calendar">
            Calendar <ArrowRight />
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="pb-3">
        {campaigns.length === 0 ? (
          <p className="flex items-center gap-2 pb-2 text-[13px] text-muted-foreground">
            <CalendarClock className="size-4" /> Nothing starts in the next 7 days.
          </p>
        ) : (
          <ul className="-mx-2">
            {campaigns.map((c) => (
              <li key={c.id}>
                <Link to="/campaigns/$campaignId" params={{ campaignId: c.id }} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted/60">
                  <span className="size-2.5 shrink-0 rounded-full" style={{ background: c.color ?? 'var(--primary)' }} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{c.name}</span>
                    {c.advertiser && <span className="block truncate text-xs text-muted-foreground">{c.advertiser}</span>}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">Starts {formatDate(c.start_date, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

/** Fleet player software versions as a single stacked meter + legend. */
export function PlayerVersionsCard({ versions }: { versions: { version: string | null; count: number }[] | undefined }) {
  const shares = versionShares(versions ?? [])
  if (shares.length === 0) return null
  const tones = ['bg-primary', 'bg-primary/70', 'bg-primary/45', 'bg-primary/25', 'bg-muted-foreground/30']
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Cpu className="size-4 text-muted-foreground" /> Player versions
        </CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/players">
            Players <ArrowRight />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex h-2 gap-0.5 overflow-hidden rounded-full" role="img" aria-label="Player version distribution">
          {shares.map((s, i) => (
            <span key={s.label} className={tones[i]} style={{ width: `${s.share * 100}%` }} />
          ))}
        </div>
        <ul className="mt-3 space-y-1.5 text-[13px]">
          {shares.map((s, i) => (
            <li key={s.label} className="flex items-center gap-2">
              <span className={cn('size-2 rounded-sm', tones[i])} aria-hidden />
              <span className="flex-1 font-mono text-xs">{s.label}</span>
              <span className="tabular text-muted-foreground">
                {s.count} · {Math.round(s.share * 100)}%
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
